import { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import * as actions from '@actions'
import useCheckableItems from '@hook/useCheckableItems'
import { toast } from 'react-toastify'
import { CHECK_FIELDS, CHECK_DETAIL_FIELDS, ROUTE_PATHS } from '@constants'
import cn from 'clsx'

import CheckRow from '../CheckRow'
import CheckDetailsEditRow from './CheckDetailsEditRow'
import Checkbox from '$components/Forms/Checkbox'
import ConfirmationModal from '@components/shared/ConfirmationModal'
import LazyImage from '@components/shared/LazyImage'
import CircleSpinner from '@components/shared/CircleSpinner'
import Pagination from '@components/shared/Pagination'
import { MdOpenInNew, MdDeleteSweep } from 'react-icons/md'

const CheckDetailsEditTable = ({
    className,
    loading,
    check,
    pageIndex,
    setPageIndex,
    details,
    currentPageImageUrl,
}) => {
    
    const refPagePreviewWindow = useRef()
    const [deleteCheckDetailsModalOpen, setDeleteCheckDetailsModalOpen] = useState(false)
    
    const checkPage = useMemo(() => check?.pages?.[pageIndex], [check, pageIndex])
    
    const {
        checked,
        allChecked,
        someChecked,
        hasChecked,
        toggleChecked,
        toggleAllChecked,
    } = useCheckableItems(checkPage?.details ?? [])
    
    const previewWindowUrl = useMemo(() => {
        
        if (!check) return null
        
        return ROUTE_PATHS.CHECK_PREVIEW_BY_PAGE
            .replace(':checkId', check.id)
            .replace(':pageIndex', pageIndex || 0)
        
    }, [check, pageIndex])
    
    const deleteSelectedCheckDetails = async () => {
        
        try {
            
            await Promise.all(checked.map(it => actions.deleteCheck(it)))
            await actions.fetchChecks()
            
            toast.success('Checks deleted')
            
        } catch (e) {
            
            console.error('deleteSelectedChecks', e)
            
            toast.error('Failed to delete checks')
            
        }
        
    }
    
    const openPreviewWindow = useCallback(() => {
        
        if (!previewWindowUrl) return
        
        refPagePreviewWindow.current = window.open(
            previewWindowUrl,
            'parseCheckPreview',
            'popup,width=1024,height=768,left=100,top=100',
        )
        
    }, [previewWindowUrl])
    
    // Automatically update the preview window URL on page change
    useEffect(() => {
        
        if (!previewWindowUrl) return
        
        refPagePreviewWindow.current?.location?.replace(previewWindowUrl)
        
    }, [refPagePreviewWindow, check, pageIndex])
    
    return (<>
        
        <div className={cn('CheckDetailsEditTable-v-split', className)}>
            
            <div className="w-full overflow-x-auto shrink-0">
                
                <div className="flex gap-4">
                    
                    <div className="overflow-auto shrink-0">
                        <h4 className="mb-2 table-header">
                            Check Preview
                        </h4>
                        <div className="page-preview-link min-w-[160px] min-h-[128px] overflow-hidden">
                            <LazyImage src={currentPageImageUrl} delayMillis={500} />
                            <div className="absolute-centered icon-wrap" onClick={openPreviewWindow}>
                                <MdOpenInNew className="text-2xl absolute-centered icon" />
                            </div>
                            {loading && (
                                <div className="!opacity-100 absolute-centered icon-wrap !bg-slate-700/50">
                                    <CircleSpinner className="absolute-centered" />
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="grow">
                        <h4 className="mb-2 table-header">
                            Check Summary
                        </h4>
                        <table className="table-auto CheckSummaryTable summary">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    {Object.keys(CHECK_FIELDS).map(it => (
                                        <th key={it}>{it}</th>
                                    ))}
                                    <th>
                                        {(allChecked || someChecked) && (
                                            <div className="flex items-center content-center justify-center">
                                                <button
                                                    className="btn"
                                                    onClick={() => setDeleteCheckDetailsModalOpen(true)}>
                                                    <MdDeleteSweep />
                                                </button>
                                            </div>
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <CheckRow
                                    key={check.id}
                                    check={check}
                                    showMeta={false}
                                    showDelete={false} />
                            </tbody>
                        </table>
                    </div>
                
                </div>
            
            </div>
            
            <header className="flex items-center content-center justify-between mt-4">
                <h4 className="table-header">
                    <span>Check Details</span>
                </h4>
                <div>
                    <Pagination
                        count={check?.pages?.length}
                        itemsPerPage={100}
                        setPageIndex={setPageIndex} />
                </div>
            </header>
            
            <div className="CheckDetailsEditTable-wrap">
                
                <table className={cn('CheckDetailsEditTable table-pin-rows details', className)}>
                    <thead>
                        <tr>
                            <th className="w-2 px-2">
                                <Checkbox
                                    checked={allChecked}
                                    indeterminate={someChecked}
                                    onChange={toggleAllChecked} />
                            </th>
                            {Object.keys(CHECK_DETAIL_FIELDS).map(it => (
                                <th key={it} className="w-full break-keep whitespace-nowrap">
                                    {it}
                                </th>
                            ))}
                            <th>
                                {(allChecked || someChecked) && (
                                    <div className="flex items-center content-center justify-center">
                                        <button
                                            className="btn"
                                            onClick={() => setDeleteCheckDetailsModalOpen(true)}>
                                            <MdDeleteSweep />
                                        </button>
                                    </div>
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {details?.map(it => (
                            <CheckDetailsEditRow
                                key={it.id}
                                details={it}
                                hasChecked={hasChecked}
                                toggleChecked={toggleChecked} />
                        ))}
                    </tbody>
                </table>
            </div>
        
        </div>
        
        <ConfirmationModal
            showModal={deleteCheckDetailsModalOpen}
            showHideModal={setDeleteCheckDetailsModalOpen}
            title="Delete Check Details"
            onConfirm={deleteSelectedCheckDetails}
            message={<p>Are you sure you want to delete these check detail rows?</p>} />
    
    </>)
    
}

export default CheckDetailsEditTable
